//   Security conscious Sentry client that
//   - Disables breadcrumbs by default (user may override)
//   - Sanitizes sensitive data keys by default (user may override)
//   - Disables all urls by default (user must override)
//   - Disables session tracking by default (user cannot override)
//   - Sets a default list of common ignore rules (user can extend)
//   https://docs.sentry.io/platforms/javascript/configuration/options/#common-options

import { init as sentryInit, breadcrumbsIntegration } from '@sentry/browser';
import type { BrowserOptions } from '@sentry/browser';
import { createDataScrubberIntegration } from './dataScrubber';
import type { Integration } from '@sentry/core';
import { defaultErrorsToIgnore, defaultKeysToSanitize } from './defaults';

/**
 * Wrapper around Sentry.init that sets up a security conscious Sentry client.
 *
 * In practice this is the same as using Sentry.init, but with `createDataScrubberIntegration`
 * integration.
 */
export function init(options?: BrowserOptions): void {
  // Check if the user overrode allowUrls, which is necessary to receive any alerts
  if (options?.allowUrls === undefined) {
    throw new Error("Sentry.init: Missing 'allowUrls' option");
  }

  // User requested integrations
  const userIntegrations = options?.integrations;

  /** Inserts our own integrations and remove others */
  function mergeIntegrations(integrations: Integration[]): Integration[] {
    // integrations will be all default integrations
    // Remove the Breadcrumbs integration by default (the user may explicitly choose to enable this)
    let newIntegrations = integrations.filter(
      integration =>
        integration.name !== breadcrumbsIntegration.name && integration.name !== 'Breadcrumbs'
    );

    // NOTE: Dedupe is standard now and is enabled by default.

    // Add the data sanitization integration for everyone
    newIntegrations.push(
      createDataScrubberIntegration({
        sanitizeKeys: defaultKeysToSanitize,
      })
    );

    // Allow the user to add integrations
    if (Array.isArray(userIntegrations)) {
      newIntegrations = newIntegrations.concat(userIntegrations);
    }

    // Allow the user to modify integrations
    if (userIntegrations instanceof Function) {
      newIntegrations = userIntegrations(newIntegrations);
    }

    return newIntegrations;
  }

  sentryInit({
    // restrict all urls by default by putting in a placeholder (the user must override this)
    allowUrls: ['block_all'],
    ...options,
    integrations: mergeIntegrations,
    // force disable session tracking
    autoSessionTracking: false,
    // ignore default set of errors plus what the user wants
    ignoreErrors: defaultErrorsToIgnore.concat(options?.ignoreErrors ?? []),
  } as BrowserOptions);
}
